import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  activeValue: string;
  idPrefix: string;
  selectTab: (nextValue: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(componentName: string): TabsContextValue {
  const context = useContext(TabsContext);
  if (context === null) {
    throw new Error(componentName + ' must be rendered inside a Tabs component');
  }
  return context;
}

function buildTriggerId(idPrefix: string, value: string): string {
  return idPrefix + '-tab-' + value;
}

function buildPanelId(idPrefix: string, value: string): string {
  return idPrefix + '-tabpanel-' + value;
}

interface TabsProps {
  value: string;
  onValueChange: (nextValue: string) => void;
  idPrefix: string;
  children: ReactNode;
  className?: string;
}

export function Tabs({ value, onValueChange, idPrefix, children, className }: TabsProps) {
  return (
    <TabsContext.Provider value={{ activeValue: value, idPrefix, selectTab: onValueChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}

export function TabsList({ ariaLabel, children, className }: TabsListProps) {
  const listElementRef = useRef<HTMLDivElement>(null);

  function focusAndSelectTab(tabElement: HTMLButtonElement) {
    tabElement.focus();
    tabElement.click();
  }

  function handleKeyDown(keyboardEvent: KeyboardEvent<HTMLDivElement>) {
    const listElement = listElementRef.current;
    if (listElement === null) {
      return;
    }
    const tabElements = Array.from(
      listElement.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)')
    );
    if (tabElements.length === 0) {
      return;
    }
    const currentIndex = tabElements.findIndex(function isFocused(tabElement) {
      return tabElement === document.activeElement;
    });
    function focusAndSelectTabAt(index: number) {
      const tabElement = tabElements[index];
      if (tabElement !== undefined) {
        focusAndSelectTab(tabElement);
      }
    }
    if (keyboardEvent.key === 'ArrowRight') {
      keyboardEvent.preventDefault();
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % tabElements.length;
      focusAndSelectTabAt(nextIndex);
      return;
    }
    if (keyboardEvent.key === 'ArrowLeft') {
      keyboardEvent.preventDefault();
      const previousIndex =
        currentIndex === -1
          ? tabElements.length - 1
          : (currentIndex - 1 + tabElements.length) % tabElements.length;
      focusAndSelectTabAt(previousIndex);
      return;
    }
    if (keyboardEvent.key === 'Home') {
      keyboardEvent.preventDefault();
      focusAndSelectTabAt(0);
      return;
    }
    if (keyboardEvent.key === 'End') {
      keyboardEvent.preventDefault();
      focusAndSelectTabAt(tabElements.length - 1);
    }
  }

  return (
    <div
      ref={listElementRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative flex gap-0.5 sm:gap-1 overflow-x-auto border-b border-zooborder [-webkit-overflow-scrolling:touch] [scrollbar-width:none]',
        className
      )}
    >
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

export function TabsTrigger({ value, children, disabled = false, className }: TabsTriggerProps) {
  const { activeValue, idPrefix, selectTab } = useTabsContext('TabsTrigger');
  const isActive = value === activeValue;
  const triggerElementRef = useRef<HTMLButtonElement>(null);

  useEffect(
    function revealActiveTrigger() {
      const triggerElement = triggerElementRef.current;
      if (isActive === false || triggerElement === null) {
        return;
      }
      revealTriggerWithinList(triggerElement);
    },
    [isActive]
  );

  return (
    <button
      ref={triggerElementRef}
      type="button"
      role="tab"
      id={buildTriggerId(idPrefix, value)}
      aria-selected={isActive}
      aria-controls={buildPanelId(idPrefix, value)}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={function selectThisTab() {
        selectTab(value);
      }}
      className={cn(
        'px-3 sm:px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zooprimary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
        isActive
          ? 'border-zooprimary text-zooprimary'
          : 'border-transparent text-zoomuted hover:text-zooindigo',
        className
      )}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { activeValue, idPrefix } = useTabsContext('TabsContent');
  if (value !== activeValue) {
    return null;
  }
  return (
    <div
      role="tabpanel"
      id={buildPanelId(idPrefix, value)}
      aria-labelledby={buildTriggerId(idPrefix, value)}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}

function revealTriggerWithinList(triggerElement: HTMLButtonElement) {
  const listElement = triggerElement.parentElement;
  if (listElement === null) {
    return;
  }
  const triggerStart = triggerElement.offsetLeft;
  const triggerEnd = triggerStart + triggerElement.offsetWidth;
  if (triggerStart < listElement.scrollLeft) {
    listElement.scrollLeft = triggerStart;
    return;
  }
  const visibleEnd = listElement.scrollLeft + listElement.clientWidth;
  if (triggerEnd > visibleEnd) {
    listElement.scrollLeft = triggerEnd - listElement.clientWidth;
  }
}
